import type { ChangeEvent } from 'react';
import { ChevronDown, Copy, Paperclip, Send, Trash2, X } from 'lucide-react';
import styles from './InternalNotesShared.module.css';

export const INTERNAL_NOTE_TAG_OPTIONS = [
  'general',
  'tuition',
  'admissions',
  'financial_aid',
  'medical',
  'other',
] as const;

export type InternalNoteTag = (typeof INTERNAL_NOTE_TAG_OPTIONS)[number];

const INTERNAL_NOTE_TAG_LABELS: Record<InternalNoteTag, string> = {
  general: 'General',
  tuition: 'Tuition',
  admissions: 'Admissions',
  financial_aid: 'Financial Aid',
  medical: 'Medical',
  other: 'Other',
};

export const normalizeInternalNoteTag = (value: unknown): InternalNoteTag => {
  const normalized = String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_');

  if (normalized === 'financialaid') {
    return 'financial_aid';
  }

  if (INTERNAL_NOTE_TAG_OPTIONS.includes(normalized as InternalNoteTag)) {
    return normalized as InternalNoteTag;
  }

  return 'general';
};

export const formatInternalNoteTag = (tag: InternalNoteTag): string =>
  INTERNAL_NOTE_TAG_LABELS[tag];

type InternalNotesComposerAttachment = {
  id: string;
  label: string;
  onRemove: () => void;
  dataTestId?: string;
};

type InternalNotesComposerCardProps = {
  noteText: string;
  onNoteTextChange: (value: string) => void;
  noteTag: InternalNoteTag;
  onNoteTagChange: (value: InternalNoteTag) => void;
  placeholder: string;
  currentDateLabel: string;
  onSave: () => void | Promise<void>;
  isSaving?: boolean;
  isDisabled?: boolean;
  attachmentButton?: {
    dataTestId?: string;
    onClick: () => void;
    title?: string;
  };
  attachments?: InternalNotesComposerAttachment[];
};

export function InternalNotesComposerCard({
  noteText,
  onNoteTextChange,
  noteTag,
  onNoteTagChange,
  placeholder,
  currentDateLabel,
  onSave,
  isSaving = false,
  isDisabled = false,
  attachmentButton,
  attachments = [],
}: InternalNotesComposerCardProps) {
  const saveDisabled = isDisabled || noteText.trim().length === 0;

  return (
    <div
      data-testid="internal-notes-composer-card"
      className={styles.composerCard}
    >
      <textarea
        value={noteText}
        onChange={(event: ChangeEvent<HTMLTextAreaElement>) =>
          onNoteTextChange(event.target.value)
        }
        className={styles.textarea}
        placeholder={placeholder}
      />

      <div className={styles.footer}>
        <div className={styles.footerLeft}>
          <span className={styles.dateLabel}>{currentDateLabel}</span>
          {attachmentButton ? (
            <button
              type="button"
              data-testid={attachmentButton.dataTestId}
              onClick={attachmentButton.onClick}
              className={styles.attachmentButton}
              title={attachmentButton.title ?? 'Attach files'}
            >
              <Paperclip size={20} />
            </button>
          ) : null}
        </div>

        <div className={styles.footerRight}>
          <div className={styles.tagSelectWrap}>
            <select
              data-testid="internal-notes-tag-select"
              value={noteTag}
              onChange={(event) =>
                onNoteTagChange(event.target.value as InternalNoteTag)
              }
              className={styles.tagSelect}
            >
              {INTERNAL_NOTE_TAG_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {formatInternalNoteTag(option)}
                </option>
              ))}
            </select>
            <ChevronDown size={14} className={styles.tagSelectChevron} />
          </div>

          <button
            type="button"
            onClick={() => {
              void onSave();
            }}
            disabled={saveDisabled}
            className={`${styles.saveButton} ${saveDisabled ? styles.saveButtonDisabled : ''}`}
          >
            <Send size={16} />
            {isSaving ? 'Saving…' : 'Save Note'}
          </button>
        </div>
      </div>

      {attachments.length > 0 ? (
        <div className={styles.attachments}>
          {attachments.map((attachment) => (
            <div key={attachment.id} className={styles.attachmentRow}>
              <span
                data-testid={attachment.dataTestId}
                className={styles.attachmentLabel}
              >
                "{attachment.label}"
              </span>
              <button
                type="button"
                onClick={attachment.onRemove}
                className={styles.attachmentRemove}
                aria-label={`Remove ${attachment.label}`}
              >
                <X size={12} />
              </button>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

export type InternalNotesListItem = {
  id: string;
  authorName: string;
  createdAtLabel: string;
  content: string;
  tag: InternalNoteTag;
  important?: boolean;
  onCopy?: () => void | Promise<void>;
  onDelete?: () => void | Promise<void>;
};

type InternalNotesListSectionProps = {
  notes: InternalNotesListItem[];
  emptyMessage?: string;
};

export function InternalNotesListSection({
  notes,
  emptyMessage = 'No notes yet.',
}: InternalNotesListSectionProps) {
  return (
    <div className={`${styles.listSection} ${styles.listSectionOffset}`}>
      <h3 className={styles.listTitle}>All Notes</h3>
      {notes.length === 0 ? (
        <p className={styles.emptyState}>{emptyMessage}</p>
      ) : null}

      <ul data-testid="financial-aid-notes-list" className={styles.noteList}>
        {notes.map((note) => (
          <li
            key={note.id}
            data-testid="financial-aid-note-item"
            className={`${styles.noteItem} ${note.important ? styles.noteItemImportant : ''}`}
          >
            <div className={styles.noteHeader}>
              <span className={styles.noteAuthor}>
                {note.authorName || 'Staff'}
              </span>
              <div className={styles.noteMetaActions}>
                <span className="px-2.5 py-1 rounded-lg text-[10px] font-bold tracking-wide bg-primary/10 text-primary">
                  {formatInternalNoteTag(note.tag)}
                </span>
                <span className={styles.noteDate}>
                  {note.createdAtLabel || 'Recent'}
                </span>
                {note.onCopy ? (
                  <button
                    type="button"
                    className={`${styles.noteActionButton} ${styles.noteActionButtonPrimary}`}
                    onClick={() => {
                      void note.onCopy?.();
                    }}
                    title="Copy note"
                  >
                    <Copy size={14} />
                  </button>
                ) : null}
                {note.onDelete ? (
                  <button
                    type="button"
                    className={`${styles.noteActionButton} ${styles.noteActionButtonDanger}`}
                    onClick={() => {
                      void note.onDelete?.();
                    }}
                    title="Delete note"
                  >
                    <Trash2 size={14} />
                  </button>
                ) : null}
              </div>
            </div>
            <p className={styles.noteContent}>{note.content}</p>
          </li>
        ))}
      </ul>
    </div>
  );
}
